import { isMobileSafari } from '../utils';

/**
 * @description BasicInput extending
 * @param Widget BasicInput widget for extending
 * @returns BasicInputExt class
 */

function BasicInputExtClassCreator(BasicInput: import('widgets/forms/BasicInput').TBasicInput) {
    /**
     * @category widgets
     * @subcategory forms
     * @class BasicInputExt
     * @augments Widget
     * @classdesc BasicInputExt Widget extends BasicInput widget, added functionality for identifying if input is empty or not when focused, adding/removing class `m-focus` to label
     */
    class BasicInputExt extends BasicInput {
        prefs() {
            return {
                classesSuccess: 'm-success',
                classesSSrOnly: 'b-sr_only',
                classesRequired: 'm-required',
                showSuccessMessage: false,
                defaultSuccessMessage: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.checkValue();
        }

        /**
         * @description Returns input value
         */
        getValue(): string {
            return this.ref('field').val();
        }

        /**
         * @description Handles `focus` event
         * @listens dom#focus
         */
        handleFocus() {
            this.ref('label').addClass('m-focus');
        }

        /**
         * @description Handles `blur` event
         * @listens dom#blur
         */
        handleBlur() {
            this.ref('label').removeClass('m-focus');
        }

        /**
         * @description Set focus to input and scroll element into viewport
         */
        setFocus() {
            const field = this.ref('field');

            // on mobile safari browser not automatically scrolls to the focused select input
            if (isMobileSafari() && field.get()?.tagName.toLowerCase() === 'select') {
                const elementToScroll = this.ref('self').get();

                elementToScroll?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                this.focus();
            } else {
                super.setFocus();
            }
        }

        /**
         * @description Checks input value`
         */
        checkValue() {
            const refField = this.ref('field');

            if (refField.val() === '') {
                this.ref('label').removeClass('m-focus');
                this.ref('field').addClass('m-empty');
            } else {
                this.ref('label').addClass('m-focus');
                this.ref('field').removeClass('m-empty');
            }
        }

        /**
         * @description Validate input and trigger `change` event
         * @emits BasicInput#change
         */
        validate() {
            // Trim input value if it is an input tag
            // This is needed to avoid validation issues with leading/trailing spaces
            if (this.isInputTextTag()) {
                const value = this.ref('field').val();

                if (value) {
                    this.ref('field').val(value.trim());
                }
            }

            this.checkValue();

            const isValid = super.validate();

            if (isValid) {
                this.setSuccessMsg();
            } else {
                this.ref('errorFeedback').removeClass([this.prefs().classesSuccess, this.prefs().classesSSrOnly]);
            }

            return isValid;
        }

        /**
         * @description Checks if the field is an input text element.
         */
        isInputTextTag() {
            const types = ['hidden', 'radio', 'checkbox'];
            const field = this.ref('field');

            return (field.get()?.tagName.toLowerCase() as string) === 'input' && !types.includes(field.attr('type'));
        }

        setSuccessMsg() {
            const successMessage = this.prefs().validationConfig?.errors?.successMessage;

            if (this.prefs().showSuccessMessage && successMessage) {
                this.ref('field').addClass(this.prefs().classesSuccess);
                this.ref('field').removeClass(this.prefs().classesErrorState);
                this.ref('errorFeedback')
                    .removeClass(this.prefs().classesSSrOnly)
                    .addClass(this.prefs().classesSuccess)
                    .setText(successMessage)
                    .show();
            } else {
                this.ref('errorFeedback')
                    .addClass([this.prefs().classesSuccess, this.prefs().classesSSrOnly])
                    .setText(this.prefs().defaultSuccessMessage)
                    .show();
            }
        }

        /**
         * @description Set input value
         * "checkRanges" method needs to validate min\max length after changing input value by JS
         * https://stackoverflow.com/questions/53226031/html-input-validity-not-checked-when-value-is-changed-by-javascript
         * @param [newVal] - set this value to input
         * @param [silently] - if set to `true` - input
         * should not be validated against a new value and no events should be fired
         */
        setValue(newVal: string | number | undefined = '', silently: boolean | undefined = false) {
            const refField = this.ref('field');
            const field = refField.get();

            refField.val(String(newVal));

            if (field instanceof HTMLSelectElement) {
                const selectedIndex = field.options.selectedIndex;

                Array.from(field.options).forEach((option, index) => {
                    const isSelected = selectedIndex > 0 && index === selectedIndex;

                    option.toggleAttribute('selected', isSelected);
                });
            }

            this.checkRanges(refField);

            if (!silently) {
                this.update();
            }

            this.setInputInteractedState();
        }

        /**
         * @description Set/unset error state into input (message and classes)
         * @param [error] error message - if defined, will set error state, and valid state - otherwise
         * @emits BasicInput#inputstatechanged
         */
        setError(error?: string) {
            if (error) {
                this.ref('errorFeedback').removeClass([this.prefs().classesSuccess, this.prefs().classesSSrOnly]);
            }

            if (error) {
                this.ref('field').removeClass(this.prefs().classesValidState).addClass(this.prefs().classesErrorState).attr('aria-invalid', 'true');
                this.ref('self').removeClass(this.prefs().classesWrapperValidState).addClass(this.prefs().classesWrapperErrorState);
                this.ref('errorFeedback').setHtml(error).show();
            } else {
                this.ref('field').removeClass(this.prefs().classesErrorState).addClass(this.prefs().classesValidState).attr('aria-invalid', 'false');
                this.ref('self').removeClass(this.prefs().classesWrapperErrorState).addClass(this.prefs().classesWrapperValidState);
                this.ref('errorFeedback').hide();
            }

            /**
             * @description Event, indicates input state was changed
             * @event BasicInput#inputstatechanged
             */
            this.emit('inputstatechanged', this, error || null);
        }

        /**
         * @description Set input as mandatory
         */
        mandatory() {
            this.ref('self').addClass(this.prefs().classesRequired);
            this.ref('field').attr('required', true);
            this.ref('optionalMark').hide();
            this.ref('requiredMark').show();
            this.clearError();
        }

        /**
         * @description Set input as optional
         */
        optional() {
            this.ref('self').removeClass(this.prefs().classesRequired);
            this.ref('field').attr('required', false);
            this.ref('optionalMark').show();
            this.ref('requiredMark').hide();
            this.clearError();
        }
    }

    return BasicInputExt;
}

export type TBasicInputExt = ReturnType<typeof BasicInputExtClassCreator>;

export type TBasicInputExtInstance = InstanceType<TBasicInputExt>;

export default BasicInputExtClassCreator;
